import { FormEvent, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Reveal } from "./Reveal";
import { useEnquiry } from "../context/EnquiryContext";
import { EnquiryFields, buildEnquiryMessage } from "../lib/whatsapp";
import { SITE, waLink } from "../lib/site";
import { WhatsAppIcon } from "./icons";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const MAX_FILES = 5;
const MAX_SIZE = 10 * 1024 * 1024;
const UNITS = [
  { v: "cm", l: "Centimetres" },
  { v: "ft", l: "Feet" },
  { v: "in", l: "Inches" },
];
const SHAPES = ["Rectangle", "Circle", "Oval", "Custom"];
const CATEGORIES = ["Characters", "Gaming", "Words & Names", "Abstract", "Logos", "Something else"];
const BUDGETS = [
  "Under ₹5,000",
  "₹5,000 – ₹10,000",
  "₹10,000 – ₹20,000",
  "₹20,000 – ₹50,000",
  "Above ₹50,000",
  "Let's discuss",
];
const QUICK_COLOURS = ["Forest green", "Ivory", "Burgundy", "Antique gold", "Mustard", "Blush pink", "Charcoal", "Sky blue"];

const BLANK: EnquiryFields = {
  name: "",
  city: "",
  postcode: "",
  category: "",
  reference: "",
  width: "",
  height: "",
  unit: "cm",
  shape: "",
  colours: "",
  wording: "",
  budget: "",
  preferredDate: "",
  description: "",
};

interface FileItem {
  file: File;
  url: string;
  error?: string;
}

type Status = "idle" | "submitting" | "success" | "error";

interface Result {
  enquiry_id: string;
  reference_token: string;
  storage_ok: boolean;
  file_errors: string[];
}

function validateFile(f: File): string | undefined {
  const okType =
    ["image/jpeg", "image/png", "image/webp"].includes(f.type) ||
    /\.(jpe?g|png|webp)$/i.test(f.name);
  if (!okType) return "Only JPG, PNG or WebP images are supported.";
  if (f.size > MAX_SIZE) return "This file is larger than 10 MB.";
  return undefined;
}

export function EnquiryForm() {
  const { reference, clearReference } = useEnquiry();
  const [fields, setFields] = useState<EnquiryFields>(BLANK);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [shape, setShape] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [fileItems, setFileItems] = useState<FileItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [progress, setProgress] = useState(0);
  const [serverError, setServerError] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Keep the gallery reference in sync with the shared context.
  useEffect(() => {
    setFields((f) => ({ ...f, reference: reference ? reference.title : "" }));
  }, [reference]);

  const setField = (k: keyof EnquiryFields, v: string) => setFields((f) => ({ ...f, [k]: v }));

  const addFiles = (list: FileList | File[]) => {
    const incoming = Array.from(list);
    setFileItems((prev) => {
      const room = MAX_FILES - prev.length;
      if (room <= 0) {
        toast.error(`You can attach up to ${MAX_FILES} images.`);
        return prev;
      }
      if (incoming.length > room) toast.error(`Only ${MAX_FILES} images per enquiry — extra files were skipped.`);
      return [
        ...prev,
        ...incoming.slice(0, room).map((f) => ({ file: f, url: URL.createObjectURL(f), error: validateFile(f) })),
      ];
    });
  };

  const removeFile = (idx: number) => {
    setFileItems((prev) => {
      URL.revokeObjectURL(prev[idx].url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const addQuickColour = (c: string) => {
    setFields((f) => {
      const cur = f.colours.split(",").map((s) => s.trim()).filter(Boolean);
      if (cur.some((s) => s.toLowerCase() === c.toLowerCase())) return f;
      return { ...f, colours: [...cur, c].join(", ") };
    });
  };

  const waHref = (enquiryId: string, token: string): string => {
    const refUrl = token ? `${window.location.origin}/r/${token}` : null;
    return waLink(
      buildEnquiryMessage({ ...fields, name: fields.name.trim() || "—" }, enquiryId, refUrl)
    );
  };

  const currentHref = result ? waHref(result.enquiry_id, result.reference_token) : "";

  const copyMessage = () => {
    if (!result) return;
    const msg = buildEnquiryMessage({ ...fields, name: fields.name.trim() || "—" }, result.enquiry_id, result.reference_token ? `${window.location.origin}/r/${result.reference_token}` : null);
    navigator.clipboard
      .writeText(msg)
      .then(() => toast.success("Message copied — paste it into WhatsApp."))
      .catch(() => toast.error("Couldn't copy — long-press the message preview to copy it."));
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (status === "submitting") return;
    const errs: string[] = [];
    if (!fields.name.trim()) errs.push("Please tell us your name.");
    const digits = phone.replace(/[^0-9]/g, "");
    if (digits.length < 8 || digits.length > 15)
      errs.push("Please add your WhatsApp number with country code (e.g. +91 98765 43210).");
    if (fileItems.some((f) => f.error)) errs.push("Remove or replace the highlighted files before sending.");
    setFormErrors(errs);
    if (errs.length) return;

    const fd = new FormData();
    fd.append("name", fields.name);
    fd.append("whatsapp", phone);
    fd.append("email", email);
    fd.append("city", fields.city);
    fd.append("postcode", fields.postcode);
    fd.append("category", fields.category);
    fd.append("width", fields.width);
    fd.append("height", fields.height);
    fd.append("unit", fields.unit);
    fd.append("shape", shape);
    fd.append("colours", fields.colours);
    fd.append("wording", fields.wording);
    fd.append("budget", fields.budget);
    fd.append("preferred_date", fields.preferredDate);
    fd.append("description", fields.description);
    fd.append("reference", reference ? reference.title : "");
    fd.append("company", honeypot);
    fileItems.forEach((f) => fd.append("files", f.file, f.file.name));

    const xhr = new XMLHttpRequest();
    setStatus("submitting");
    setServerError("");
    setProgress(0);
    xhr.open("POST", `${API}/api/enquiries`);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) setProgress(ev.loaded / ev.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const res: Result = JSON.parse(xhr.responseText);
          setResult(res);
          setStatus("success");
          toast.success("Enquiry saved.");
        } catch {
          setServerError("We saved something unexpected — please try again.");
          setStatus("error");
        }
      } else if (xhr.status === 429) {
        setServerError("You're sending enquiries a little too quickly — please try again in a few minutes.");
        setStatus("error");
      } else if (xhr.status === 422) {
        let detail = "Please check the form and try again.";
        try {
          const d = JSON.parse(xhr.responseText);
          if (typeof d.detail === "string") detail = d.detail;
        } catch { /* keep default */ }
        setServerError(detail);
        setStatus("error");
      } else {
        setServerError("Something went wrong saving your enquiry — please try again. Your details are still here.");
        setStatus("error");
      }
    };
    xhr.onerror = () => {
      setServerError("We couldn't reach the studio — check your connection and try again. Your details are preserved.");
      setStatus("error");
    };
    xhr.send(fd);
  };

  const resetAll = () => {
    fileItems.forEach((f) => URL.revokeObjectURL(f.url));
    setFileItems([]);
    setFields(BLANK);
    setPhone("");
    setEmail("");
    setShape("");
    setFormErrors([]);
    setServerError("");
    setResult(null);
    setProgress(0);
    clearReference();
    setStatus("idle");
  };

  const dims = [fields.width.trim(), fields.height.trim()].filter(Boolean).join(" × ");
  const sizeStr = dims ? `${dims} ${fields.unit}` : "";

  const summary: Array<[string, string]> = [
    ["Design", [fields.category, reference?.title].filter(Boolean).join(" / ")],
    ["Size", sizeStr.trim()],
    ["Shape", shape],
    ["Colours", fields.colours],
    ["Text", fields.wording],
    ["Budget", fields.budget],
  ].filter(([, v]) => v) as Array<[string, string]>;

  return (
    <section id="customise" className="py-20 sm:py-24" data-testid="customise">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <Reveal>
          <div className="max-w-2xl">
            <p className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.22em] text-burgundy font-medium">
              <span className="inline-block h-2 w-2 rounded-full bg-brass" aria-hidden="true" />
              Customise
            </p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-forest">
              Made around your imagination.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-charcoal/75">
              Share what you have in mind. We'll discuss the design, size, and quote with you.
            </p>
          </div>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-7">
            <div className="rounded-3xl border border-forest/12 bg-ivory-soft p-5 sm:p-8 shadow-tuft">
              {status === "success" && result ? (
                <div data-testid="enquiry-success-panel" className="text-center py-4">
                  <CheckCircle2 className="h-12 w-12 text-forest mx-auto" />
                  <h3 className="mt-4 font-serif text-2xl sm:text-3xl text-forest">Your enquiry is saved.</h3>
                  <p className="mt-2 text-sm sm:text-base text-charcoal/75">
                    Send the prepared message in WhatsApp to continue.
                  </p>
                  <p className="mt-4 inline-block rounded-full bg-forest/10 px-4 py-1.5 text-sm font-medium text-forest" data-testid="enquiry-id-chip">
                    Enquiry ID: {result.enquiry_id}
                  </p>

                  {(!result.storage_ok || result.file_errors.length > 0) && (
                    <div className="mt-5 flex items-start gap-2 rounded-2xl border border-burgundy/30 bg-burgundy/5 p-4 text-left" data-testid="upload-warning">
                      <AlertTriangle className="h-4 w-4 text-burgundy mt-0.5 shrink-0" />
                      <p className="text-xs sm:text-sm text-charcoal/75">
                        {result.file_errors.length > 0 && result.file_errors.join(" ")}
                        {!result.storage_ok &&
                          " Your images couldn't be uploaded — please attach them in WhatsApp when you send the message. We never pretend an upload worked: nothing was stored."}
                      </p>
                    </div>
                  )}
                  {result.storage_ok && result.file_errors.length === 0 && fileItems.length > 0 && (
                    <p className="mt-5 rounded-2xl border border-forest/20 bg-forest/5 p-3 text-xs sm:text-sm text-forest" data-testid="upload-confirmed">
                      {fileItems.filter((f) => !f.error).length} reference image
                      {fileItems.filter((f) => !f.error).length === 1 ? "" : "s"} saved securely — the studio can view them via the private link in your message.
                    </p>
                  )}

                  <div className="mt-5 max-h-52 overflow-auto rounded-2xl bg-forest/5 p-4 text-left">
                    <pre className="whitespace-pre-wrap text-xs sm:text-sm text-charcoal/80" data-testid="whatsapp-message-preview">
                      {buildEnquiryMessage({ ...fields, name: fields.name.trim() || "—" }, result.enquiry_id, result.reference_token ? `${window.location.origin}/r/${result.reference_token}` : null)}
                    </pre>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                      href={currentHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="success-open-whatsapp"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-forest text-ivory font-medium px-6 py-3 transition-all duration-200 hover:bg-forest-deep hover:-translate-y-0.5"
                    >
                      <WhatsAppIcon />
                      Open WhatsApp
                    </a>
                    <button
                      onClick={copyMessage}
                      data-testid="success-copy-message"
                      className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-forest/25 text-forest font-medium px-6 py-3 transition-all duration-200 hover:border-forest hover:-translate-y-0.5"
                    >
                      Copy Message
                    </button>
                  </div>
                  <button
                    onClick={resetAll}
                    data-testid="success-start-another"
                    className="mt-4 text-sm text-charcoal/60 underline underline-offset-4 hover:text-forest"
                  >
                    Start another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate>
                  {reference && (
                    <div
                      className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-brass/50 bg-brass-soft px-4 py-3"
                      data-testid="selected-reference-chip"
                    >
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-burgundy font-medium">
                          Selected design
                        </p>
                        <p className="font-serif text-forest">
                          {reference.title} <span className="text-charcoal/50 text-sm">· {reference.category}</span>
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={clearReference}
                        aria-label="Remove selected design reference"
                        data-testid="clear-reference-button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-forest/20 text-forest/70 hover:text-forest"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  {formErrors.length > 0 && (
                    <div className="mb-6 rounded-2xl border border-burgundy/30 bg-burgundy/5 p-4" role="alert" data-testid="form-errors">
                      <ul className="list-disc pl-4 text-sm text-burgundy space-y-1">
                        {formErrors.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {serverError && (
                    <div className="mb-6 rounded-2xl border border-burgundy/30 bg-burgundy/5 p-4 text-sm text-burgundy" role="alert" data-testid="server-error">
                      {serverError}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="f-name">Customer name *</Label>
                      <Input id="f-name" value={fields.name} onChange={(e) => setField("name", e.target.value)} placeholder="Your name" required aria-required="true" data-testid="input-name" maxLength={120} />
                    </div>
                    <div>
                      <Label htmlFor="f-phone">WhatsApp number *</Label>
                      <Input id="f-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" inputMode="tel" required aria-required="true" data-testid="input-whatsapp" maxLength={24} />
                      <p className="mt-1 text-xs text-charcoal/50">Include your country code.</p>
                    </div>
                    <div>
                      <Label htmlFor="f-email">Email (optional)</Label>
                      <Input id="f-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="input-email" maxLength={200} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="f-city">City</Label>
                        <Input id="f-city" value={fields.city} onChange={(e) => setField("city", e.target.value)} placeholder="City" data-testid="input-city" maxLength={120} />
                      </div>
                      <div>
                        <Label htmlFor="f-postcode">Postcode</Label>
                        <Input id="f-postcode" value={fields.postcode} onChange={(e) => setField("postcode", e.target.value)} placeholder="Postcode" data-testid="input-postcode" maxLength={20} />
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="f-category">Design category</Label>
                    <Select value={fields.category} onValueChange={(v) => setField("category", v)}>
                      <SelectTrigger id="f-category" data-testid="select-category" aria-label="Design category">
                        <SelectValue placeholder="Pick a direction" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <div>
                      <Label htmlFor="f-width">Width</Label>
                      <Input id="f-width" value={fields.width} onChange={(e) => setField("width", e.target.value)} inputMode="decimal" placeholder="90" data-testid="input-width" maxLength={12} />
                    </div>
                    <div>
                      <Label htmlFor="f-height">Height</Label>
                      <Input id="f-height" value={fields.height} onChange={(e) => setField("height", e.target.value)} inputMode="decimal" placeholder="60" data-testid="input-height" maxLength={12} />
                    </div>
                    <div>
                      <Label htmlFor="f-unit">Unit</Label>
                      <Select value={fields.unit} onValueChange={(v) => setField("unit", v)}>
                        <SelectTrigger id="f-unit" data-testid="select-unit" aria-label="Unit">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNITS.map((u) => (
                            <SelectItem key={u.v} value={u.v}>{u.l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label>Shape</Label>
                    <div className="mt-1.5 flex flex-wrap gap-2" role="radiogroup" aria-label="Shape">
                      {SHAPES.map((s) => (
                        <button
                          key={s}
                          type="button"
                          role="radio"
                          aria-checked={shape === s}
                          onClick={() => setShape(s)}
                          data-testid={`shape-${s.toLowerCase()}`}
                          className={`rounded-full px-4 py-2 text-sm border transition-all duration-200 ${
                            shape === s
                              ? "bg-forest text-ivory border-forest"
                              : "bg-ivory text-charcoal/75 border-forest/15 hover:border-forest/40"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="f-colours">Preferred colours</Label>
                    <Input id="f-colours" value={fields.colours} onChange={(e) => setField("colours", e.target.value)} placeholder="e.g. forest green, mustard" data-testid="input-colours" maxLength={300} />
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {QUICK_COLOURS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => addQuickColour(c)}
                          className="rounded-full border border-forest/15 bg-ivory px-3 py-1 text-xs text-charcoal/70 transition-colors duration-200 hover:border-forest/40 hover:text-forest"
                          data-testid={`colour-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="f-wording">Desired wording or name</Label>
                      <Input id="f-wording" value={fields.wording} onChange={(e) => setField("wording", e.target.value)} placeholder='e.g. "ALEX" or "GOOD VIBES"' data-testid="input-wording" maxLength={300} />
                    </div>
                    <div>
                      <Label htmlFor="f-budget">Budget range (optional, INR)</Label>
                      <Select value={fields.budget} onValueChange={(v) => setField("budget", v)}>
                        <SelectTrigger id="f-budget" data-testid="select-budget" aria-label="Budget range">
                          <SelectValue placeholder="Let's discuss" />
                        </SelectTrigger>
                        <SelectContent>
                          {BUDGETS.map((b) => (
                            <SelectItem key={b} value={b}>{b}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="f-date">Preferred completion date (optional)</Label>
                    <Input id="f-date" type="date" value={fields.preferredDate} onChange={(e) => setField("preferredDate", e.target.value)} data-testid="input-date" />
                    <p className="mt-1 text-xs text-charcoal/50">Tell us the date you have in mind — we'll confirm what's possible.</p>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="f-desc">Describe your idea</Label>
                    <Textarea id="f-desc" value={fields.description} onChange={(e) => setField("description", e.target.value)} rows={4} placeholder="A round rug of my cat wearing sunglasses, forest green background, about a metre wide…" data-testid="input-description" maxLength={2000} />
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="f-files">Reference uploads</Label>
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        addFiles(e.dataTransfer.files);
                      }}
                      data-testid="upload-dropzone"
                      className={`mt-1.5 rounded-2xl border-2 border-dashed p-6 text-center transition-colors duration-200 ${
                        dragOver ? "border-forest bg-forest/5" : "border-forest/25 bg-ivory"
                      }`}
                    >
                      <UploadCloud className="h-8 w-8 text-forest/60 mx-auto" />
                      <p className="mt-2 text-sm text-charcoal/70">Drag & drop your images here, or</p>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="upload-browse-button"
                        className="mt-2 inline-flex items-center gap-2 rounded-full bg-forest text-ivory text-sm font-medium px-5 py-2.5 transition-all duration-200 hover:bg-forest-deep"
                      >
                        Choose photos
                      </button>
                      <input
                        ref={fileInputRef}
                        id="f-files"
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        multiple
                        className="sr-only"
                        onChange={(e) => {
                          if (e.target.files) addFiles(e.target.files);
                          e.target.value = "";
                        }}
                        aria-label="Choose reference photos"
                      />
                      <p className="mt-3 text-xs text-charcoal/50">JPG, PNG or WebP · up to {MAX_FILES} images · max 10 MB each</p>
                    </div>

                    {fileItems.length > 0 && (
                      <ul className="mt-3 space-y-2" data-testid="upload-list">
                        {fileItems.map((f, i) => (
                          <li key={i} className={`flex items-center gap-3 rounded-xl border p-2 ${f.error ? "border-burgundy/40 bg-burgundy/5" : "border-forest/15 bg-ivory"}`} data-testid={`upload-item-${i}`}>
                            <img src={f.url} alt={`Selected: ${f.file.name}`} className="h-12 w-12 rounded-lg object-cover" />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm text-charcoal/80">{f.file.name}</span>
                              <span className="block text-xs text-charcoal/50">
                                {f.file.size > 1024 * 1024 ? `${(f.file.size / (1024 * 1024)).toFixed(2)} MB` : `${Math.max(1, Math.round(f.file.size / 1024))} KB`}
                                {f.error ? ` · ${f.error}` : " · ready"}
                              </span>
                            </span>
                            <button
                              type="button"
                              onClick={() => removeFile(i)}
                              aria-label={`Remove ${f.file.name}`}
                              data-testid={`upload-remove-${i}`}
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-forest/20 text-forest/70 hover:text-burgundy hover:border-burgundy/40"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="mt-4" aria-hidden="true">
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      className="absolute -left-[9999px] h-0 w-0 opacity-0"
                    />
                  </div>

                  <p className="mt-6 rounded-2xl border border-brass/40 bg-brass-soft p-4 text-xs sm:text-sm leading-relaxed text-charcoal/70" data-testid="consent-notice">
                    When you send this enquiry, your message, contact details and any uploads are
                    used only to respond to your rug enquiry — nothing else.
                  </p>

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    data-testid="submit-enquiry-button"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-forest text-ivory font-medium px-6 py-4 transition-all duration-200 hover:bg-forest-deep hover:-translate-y-0.5 disabled:opacity-60 disabled:pointer-events-none"
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving your idea…
                      </>
                    ) : (
                      <>
                        <WhatsAppIcon />
                        Send My Idea on WhatsApp
                      </>
                    )}
                  </button>

                  {status === "submitting" && (
                    <div className="mt-3" data-testid="upload-progress">
                      <p className="text-xs text-charcoal/60 mb-1">Saving your enquiry & uploads… {Math.round(progress * 100)}%</p>
                      <div className="h-1.5 rounded-full bg-forest/10 overflow-hidden">
                        <div className="h-full bg-forest rounded-full transition-[width] duration-200" style={{ width: `${Math.round(progress * 100)}%` }} />
                      </div>
                    </div>
                  )}
                </form>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="space-y-5 lg:sticky lg:top-24">
              <div className="rounded-3xl border border-forest/12 bg-ivory-soft p-6 shadow-tuft" data-testid="rug-brief-summary">
                <p className="font-serif text-xl text-forest">Your rug brief</p>
                {summary.length === 0 ? (
                  <p className="mt-3 text-sm text-charcoal/60">
                    As you fill the form, your rug takes shape here — pick a design from the gallery
                    to get started.
                  </p>
                ) : (
                  <dl className="mt-4 space-y-2.5 text-sm">
                    {summary.map(([k, v]) => (
                      <div key={k} className="flex gap-3 justify-between border-b border-forest/10 pb-2.5 last:border-0">
                        <dt className="text-charcoal/55 shrink-0">{k}</dt>
                        <dd className="text-right text-charcoal/85 font-medium">{v}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>

              <div className="rounded-3xl bg-forest texture-weave-dark text-ivory p-6 shadow-tuft" data-testid="talk-first-card">
                <p className="font-serif text-xl">Prefer to just talk it through?</p>
                <p className="mt-2 text-sm text-ivory/75 leading-relaxed">
                  Skip the form and message the studio directly — we'll ask the right questions.
                </p>
                <a
                  href={waLink(SITE.waGenericMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="aside-whatsapp-button"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-ivory text-forest text-sm font-medium px-5 py-2.5 transition-all duration-200 hover:-translate-y-0.5 hover:bg-brass-soft"
                >
                  <WhatsAppIcon />
                  {SITE.whatsappDisplay}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
